import { useLanguage } from '@/contexts/LanguageContext';
import { useState, useMemo, useRef, useEffect } from 'react';
import { MapPin, Star, X, Check, Phone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { destinations, countryLabels, type Destination } from '@/data/destinations';
import { Badge } from '@/components/ui/badge';

const RATE_PER_KM = 2.80;
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

export type VehicleType = 'standard' | 'van' | 'vip';

export const VEHICLE_CONFIG = {
  standard: { icon: '🚗', multiplier: 1.0, label: 'Standard' },
  van: { icon: '🚐', multiplier: 1.3, label: 'Van' },
  vip: { icon: '👑', multiplier: 1.5, label: 'VIP' },
} as const;

export type PopularChip = {
  name: string;
  km: number;
  price?: number;
};

export const DYNAMIC_PRICING_DESTINATIONS = new Set([
  'Airport (SZG)',
  'Salzburg Airport (SZG)',
  'Salzburg Hauptbahnhof',
  'Salzburg Altstadt',
]);

export const hasDynamicPricing = (destinationName: string) =>
  DYNAMIC_PRICING_DESTINATIONS.has(destinationName);

export const POPULAR_CHIPS: PopularChip[] = [
  { name: 'Airport (SZG)', km: 5 },
  { name: 'Hallstatt', km: 75, price: 210 },
  { name: 'München (Munich)', km: 150, price: 420 },
  { name: 'Berchtesgaden', km: 30, price: 84 },
  { name: 'Zell am See', km: 85, price: 238 },
  { name: 'Innsbruck', km: 185, price: 518 },
  { name: 'Vienna (Wien)', km: 300, price: 840 },
  { name: 'Kaprun', km: 95, price: 266 },
  { name: 'Obertauern', km: 90, price: 252 },
  { name: 'Königssee', km: 35, price: 98 },
  { name: 'Saalbach-Hinterglemm', km: 100, price: 280 },
  { name: 'Český Krumlov', km: 205, price: 574 },
] as const;

export function useCalculator(onInteraction?: () => void) {
  const [vehicleType, setVehicleType] = useState<VehicleType>('standard');
  const [selected, setSelected] = useState<Destination | null>(null);
  const [query, setQuery] = useState('');
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const multiplier = VEHICLE_CONFIG[vehicleType].multiplier;
  const basePrice = selected ? selected.km * RATE_PER_KM : 0;
  const finalPrice = basePrice * multiplier;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    let list = destinations;
    if (activeLetter) list = list.filter(d => d.name.toUpperCase().startsWith(activeLetter));
    if (query.trim()) {
      const q = query.toLowerCase();
      list = list.filter(d => d.name.toLowerCase().includes(q));
    }
    return list;
  }, [query, activeLetter]);

  const grouped = useMemo(() => {
    const groups: Record<string, Destination[]> = {};
    for (const d of filtered) {
      if (!groups[d.country]) groups[d.country] = [];
      groups[d.country].push(d);
    }
    const order = ['AT', 'DE', 'CZ', 'IT', 'SI'];
    return order.filter(c => groups[c]).map(c => ({ country: c, items: groups[c] }));
  }, [filtered]);

  const availableLetters = useMemo(() => new Set(destinations.map(d => d.name[0].toUpperCase())), []);

  const handleSelect = (d: Destination) => {
    setSelected(d);
    setQuery(d.name);
    setIsOpen(false);
    setActiveLetter(null);
    onInteraction?.();
  };

  const handleClear = () => {
    setSelected(null);
    setQuery('');
    setActiveLetter(null);
    inputRef.current?.focus();
  };

  const handleLetterClick = (letter: string) => {
    setActiveLetter(prev => prev === letter ? null : letter);
    setQuery('');
    setSelected(null);
    setIsOpen(true);
    onInteraction?.();
  };

  const handleChipClick = (chip: PopularChip) => {
    const dest = destinations.find(d => d.name === chip.name) || { name: chip.name, km: chip.km, country: 'AT' as const, flag: '🇦🇹' };
    handleSelect(dest);
  };

  return {
    vehicleType, setVehicleType,
    selected, query, setQuery, setSelected,
    activeLetter, isOpen, setIsOpen,
    inputRef, dropdownRef,
    multiplier, basePrice, finalPrice,
    filtered, grouped, availableLetters,
    handleSelect, handleClear, handleLetterClick, handleChipClick,
    setActiveLetter,
  };
}

// --- Shared Sub-Components ---

export const PopularChips = ({ onChipClick, className = '' }: { onChipClick: (chip: PopularChip) => void; className?: string }) => (
  <div className={`flex flex-wrap gap-2 ${className}`}>
    {POPULAR_CHIPS.map(chip => (
      <button
        key={chip.name}
        onClick={() => onChipClick(chip)}
        className="px-3 py-1.5 rounded-full bg-primary/10 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors border border-primary/20"
      >
        {chip.name}
        {chip.price !== undefined && <span className="text-muted-foreground ml-1">€{chip.price}</span>}
      </button>
    ))}
  </div>
);

export const VehicleCards = ({ vehicleType, setVehicleType, compact = false }: { vehicleType: VehicleType; setVehicleType: (v: VehicleType) => void; compact?: boolean }) => {
  const { t } = useLanguage();
  return (
    <div className={`grid ${compact ? 'grid-cols-3 gap-2' : 'grid-cols-3 gap-3'}`}>
      {(['standard', 'van', 'vip'] as const).map(v => {
        const cfg = VEHICLE_CONFIG[v];
        const active = vehicleType === v;
        return (
          <button
            key={v}
            onClick={() => setVehicleType(v)}
            className={`${compact ? 'py-2 px-2' : 'py-3 px-4'} rounded-xl text-sm font-semibold transition-all border flex flex-col items-center gap-1
              ${active ? 'bg-gradient-gold text-primary-foreground border-primary shadow-gold' : 'bg-muted text-muted-foreground border-border hover:border-primary/50'}`}
          >
            <span className={compact ? 'text-base' : 'text-xl'}>{cfg.icon}</span>
            <span className="text-xs">{t.calculator[v]}</span>
            <span className="text-[10px] opacity-70">×{cfg.multiplier}</span>
          </button>
        );
      })}
    </div>
  );
};

export const TrustBadges = ({ className = '' }: { className?: string }) => {
  const { t } = useLanguage();
  return (
    <div className={`flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground ${className}`}>
      <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-primary" /> {t.calculator.fixedPrices}</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-primary" /> {t.calculator.noHiddenCosts}</span>
      <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-primary" /> 24/7</span>
      <a href="tel:+436606521071" dir="ltr" className="inline-flex items-center gap-1 text-primary hover:underline">
        <Phone className="w-3.5 h-3.5" /> +43 660 6521071
      </a>
    </div>
  );
};

export const PriceResult = ({ selected, finalPrice, basePrice, multiplier, km }: {
  selected: Destination | null; finalPrice: number; basePrice: number; multiplier: number; km: number;
}) => {
  if (!selected || km === 0 || hasDynamicPricing(selected.name)) return null;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="p-5 rounded-2xl bg-gradient-gold text-center"
    >
      <p className="text-primary-foreground/80 text-sm font-medium mb-1">
        Salzburg → {selected.flag} {selected.name}
      </p>
      <p className="text-4xl font-display font-bold text-primary-foreground">
        €{finalPrice.toFixed(2)}
      </p>
      <p className="text-primary-foreground/70 text-sm mt-2">
        {km} km × €2.80 = €{basePrice.toFixed(2)}
        {multiplier !== 1 && ` × ${multiplier} = €${finalPrice.toFixed(2)}`}
      </p>
    </motion.div>
  );
};

export const AZFilter = ({ activeLetter, availableLetters, onLetterClick }: {
  activeLetter: string | null; availableLetters: Set<string>; onLetterClick: (l: string) => void;
}) => (
  <div className="flex flex-wrap gap-1">
    {ALPHABET.map(letter => {
      const available = availableLetters.has(letter);
      const active = activeLetter === letter;
      return (
        <button
          key={letter}
          onClick={() => available && onLetterClick(letter)}
          disabled={!available}
          className={`w-8 h-8 md:w-9 md:h-9 rounded-lg text-xs font-bold transition-all
            ${active
              ? 'bg-gradient-gold text-primary-foreground shadow-gold'
              : available
                ? 'bg-muted text-foreground hover:bg-primary/10 hover:text-primary border border-border'
                : 'bg-muted/50 text-muted-foreground/40 cursor-not-allowed border border-transparent'
            }`}
        >
          {letter}
        </button>
      );
    })}
  </div>
);

export const DestinationDropdown = ({
  isOpen, selected, grouped, dropdownRef, inputRef, query, setQuery, setSelected, setIsOpen, setActiveLetter, handleSelect, handleClear, onInteraction,
}: {
  isOpen: boolean; selected: Destination | null; grouped: { country: string; items: Destination[] }[];
  dropdownRef: React.RefObject<HTMLDivElement>; inputRef: React.RefObject<HTMLInputElement>;
  query: string; setQuery: (q: string) => void; setSelected: (d: Destination | null) => void;
  setIsOpen: (o: boolean) => void; setActiveLetter: (l: string | null) => void;
  handleSelect: (d: Destination) => void; handleClear: () => void; onInteraction?: () => void;
}) => {
  const { t } = useLanguage();
  return (
    <div className="relative">
      <label className="block text-sm font-medium text-foreground mb-2">
        <MapPin className="w-4 h-4 inline mr-1 text-primary" />
        Salzburg → {t.calculator.to}
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setSelected(null); setIsOpen(true); setActiveLetter(null); onInteraction?.(); }}
          onFocus={() => setIsOpen(true)}
          placeholder="z.B. Hallstatt, München, Zell am See..."
          className="w-full px-4 py-3 pr-10 rounded-xl border border-border bg-background text-foreground focus:ring-2 focus:ring-primary/50 outline-none min-h-[44px]"
        />
        {(query || selected) && (
          <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <AnimatePresence>
        {isOpen && !selected && (
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-border bg-card shadow-xl"
          >
            {grouped.length === 0 ? (
              <div className="p-4 text-center text-muted-foreground text-sm">Keine Ergebnisse</div>
            ) : (
              grouped.map(group => (
                <div key={group.country}>
                  <div className="sticky top-0 bg-muted px-4 py-2 text-xs font-bold text-muted-foreground border-b border-border">
                    {countryLabels[group.country]}
                  </div>
                  {group.items.map(d => (
                    <button
                      key={d.name}
                      onClick={() => handleSelect(d)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-primary/5 transition-colors text-left min-h-[44px]"
                    >
                      <span className="text-base">{d.flag}</span>
                      <span className="flex-1 text-sm font-medium text-foreground">{d.name}</span>
                      {d.popular && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0 gap-0.5">
                          <Star className="w-2.5 h-2.5" /> Popular
                        </Badge>
                      )}
                      <span className="text-xs text-muted-foreground">{d.km} km</span>
                    </button>
                  ))}
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const CTAButtons = () => (
  <div className="flex gap-3">
    <a href="tel:+436606521071" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-secondary text-secondary-foreground font-semibold hover:bg-accent transition-colors text-sm min-h-[44px]">
      📞 Anrufen
    </a>
    <a href="https://wa.me/436606521071" target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-green-600 text-secondary-foreground font-semibold hover:bg-green-700 transition-colors text-sm min-h-[44px]">
      💬 WhatsApp
    </a>
  </div>
);

export { RATE_PER_KM, ALPHABET, type Destination };
